// App.js
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'react-toastify/dist/ReactToastify.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import './pages/admin/styles/admin.css';
import './App.css';

// Components
import Footer from './components/Layout/Footer';
import CustomNavbar from './components/CustomNavbar';
import AnnouncementPopup from './components/AnnouncementPopup';

// Public Pages
import Home from './pages/public_web/Home';
import About from './pages/public_web/About';
import History from './pages/public_web/History';
import Departments from './pages/public_web/Departments';
import Courses from './pages/public_web/Courses';
import Faculty from './pages/public_web/Faculty';
import Events from './pages/public_web/Events';
import Gallery from './pages/public_web/Gallery';
import Contact from './pages/public_web/Contact';
import ServiceDetail from './pages/public_web/ServiceDetail';
import OwnerProfile from './pages/public_web/OwnerProfile';
import ProjectsPage from './pages/public_web/ProjectsPage';
import PublicSchoolInfo from './pages/public_web/PublicSchoolInfo';
import LeadershipMessages from './pages/public_web/LeadershipMessages';
import AdmissionProcedureDetails from './pages/public_web/AdmissionProcedureDetails';
import PublicFacilities from './pages/public_web/PublicFacilities';
import StaffPage from './pages/public_web/StaffPage';
import CalendarOfEvents from './pages/public_web/CalendarOfEvents';
import AcademicEvents from './pages/public_web/AcademicEvents';
import ProspectusView from './pages/public_web/ProspectusView';
import VisionMission from './pages/public_web/VisionMission';
import OrganizationPublic from './pages/public_web/OrganizationPublic';
import PublicAnnouncements from './pages/public_web/PublicAnnouncements';

// Admin Pages
import AdminLogin from './pages/admin/Login';
import AdminDashboard from './pages/admin/Dashboard';
import AcademicYears from './pages/admin/AcademicYears';
import DepartmentsAdm from './pages/admin/Departments';
import AdminAbout from './pages/admin/About_college';
import MenuManager from './pages/admin/MenuManager';
import AdminHomeSlider from './pages/admin/AdminHomeSlider';
import QuickLinksManager from './pages/admin/QuickLinksManager';
import FeaturesManager from './pages/admin/FeaturesManager';
import AboutContentManager from './pages/admin/AboutContentManager';
import AnnouncementsManager from './pages/admin/AnnouncementsManager';
import TeamMembersManager from './pages/admin/TeamMembersManager';
import TestimonialsManager from './pages/admin/TestimonialsManager';
import CallToActionManager from './pages/admin/CallToActionManager';
import SiteHeaderManager from './pages/admin/SiteHeaderManager';
import ServicesManager from './pages/admin/ServicesManager';
import OwnerProfilesManager from './pages/admin/OwnerProfilesManager';
import ProjectsManager from './pages/admin/ProjectsManager';
import GalleryManager from './pages/admin/GalleryManager';
import ContactManager from './pages/admin/ContactManager';
import QuickStatsManager from './pages/admin/QuickStatsManager';
import SchoolInfoManager from './pages/admin/SchoolInfoManager';
import LeadershipMessagesManager from './pages/admin/LeadershipMessagesManager';
import CoursesManager from './pages/admin/CoursesManager';
import AdmissionProcedureManager from './pages/admin/AdmissionProcedureManager';
import FacilitiesManager from './pages/admin/FacilitiesManager';
import StaffManager from './pages/admin/StaffManager';
import CalendarEventsManager from './pages/admin/CalendarEventsManager';
import AcademicEventsManager from './pages/admin/AcademicEventsManager';
import ProspectusManagement from './pages/admin/ProspectusManagement';
import OrganizationsManager from './pages/admin/OrganizationsManager';
import ChangePassword from './pages/admin/ChangePassword';

// Check if user is authenticated
const PrivateRoute = ({ children }) => {
  const token = localStorage.getItem('admin_token');
  return token ? children : <Navigate to="/admin/login" />;
};

// Component to get current page slug from URL
const PageSlugDetector = () => {
  const location = useLocation();
  
  const getPageSlug = () => {
    const path = location.pathname;
    
    // Map routes to page slugs
    const routeMap = {
      '/': 'home',
      '/about': 'about',
      '/History': 'history',
      '/departments': 'departments',
      '/courses': 'courses',
      '/faculty': 'faculty',
      '/events': 'events',
      '/gallery': 'gallery',
      '/contact': 'contact',
      '/ServiceDetail': 'service-detail',
      '/OwnerProfile': 'owner-profile',
      '/ProjectsPage': 'projects',
      '/PublicSchoolInfo': 'school-info',
      '/LeadershipMessages': 'leadership-messages',
      '/AdmissionProcedureDetails': 'admission',
      '/PublicFacilities': 'facilities',
      '/StaffPage': 'staff',
      '/CalendarOfEvents': 'calendar',
      '/AcademicEvents': 'academic-events',
      '/ProspectusView': 'prospectus',
      '/VisionMission': 'vision-mission',
      '/OrganizationPublic': 'organization',
      '/PublicAnnouncements': 'announcements',
    };
    
    // Check for exact matches first
    if (routeMap[path]) {
      return routeMap[path];
    }
    
    // Extract slug from path (remove leading slash)
    return path.replace('/', '') || 'home';
  };
  
  const pageSlug = getPageSlug();
  console.log(`Current page slug: ${pageSlug}`);
  return <AnnouncementPopup pageSlug={pageSlug} />;
};

// Single Layout with CustomNavbar and AnnouncementPopup
const PublicLayout = ({ children }) => {
  return (
    <>
      <CustomNavbar />
      <main className="main-content">
        {children}
      </main>
      <Footer />
      {/* Add AnnouncementPopup for the current page */}
      <PageSlugDetector />
    </>
  );
};

function App() {
  return (
    <Router>
      <div className="App">
        <Routes>
          {/* ========== ADMIN ROUTES (No Layout) ========== */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/departments" element={<DepartmentsAdm />} />
          <Route path="/admin/about_college" element={<AdminAbout />} />
          <Route path="/admin/academic-years" element={<AcademicYears />} />
          <Route path="/admin/AdminHomeSlider" element={<AdminHomeSlider />} />
          <Route path="/admin/QuickLinksManager" element={<QuickLinksManager />} />
          <Route path="/admin/FeaturesManager" element={<FeaturesManager />} />
          <Route path="/admin/AboutContentManager" element={<AboutContentManager />} />
          <Route path="/admin/AnnouncementsManager" element={<AnnouncementsManager />} />
          <Route path="/admin/TeamMembersManager" element={<TeamMembersManager />} />
          <Route path="/admin/TestimonialsManager" element={<TestimonialsManager />} />
          <Route path="/admin/CallToActionManager" element={<CallToActionManager />} />
          <Route path="/admin/SiteHeaderManager" element={<SiteHeaderManager />} />
          <Route path="/admin/ServicesManager" element={<ServicesManager />} />
          <Route path="/admin/OwnerProfilesManager" element={<OwnerProfilesManager />} />
          <Route path="/admin/ProjectsManager" element={<ProjectsManager />} />
          <Route path="/admin/GalleryManager" element={<GalleryManager />} />
          <Route path="/admin/ContactManager" element={<ContactManager />} />
          <Route path="/admin/QuickStatsManager" element={<QuickStatsManager />} />
          <Route path="/admin/SchoolInfoManager" element={<SchoolInfoManager />} />
          <Route path="/admin/LeadershipMessagesManager" element={<LeadershipMessagesManager />} />
          <Route path="/admin/CoursesManager" element={<CoursesManager />} />
          <Route path="/admin/AdmissionProcedureManager" element={<AdmissionProcedureManager />} />
          <Route path="/admin/FacilitiesManager" element={<FacilitiesManager />} />
          <Route path="/admin/StaffManager" element={<StaffManager />} />
          <Route path="/admin/CalendarEventsManager" element={<CalendarEventsManager />} />
          <Route path="/admin/AcademicEventsManager" element={<AcademicEventsManager />} />
          <Route path="/admin/ProspectusManagement" element={<ProspectusManagement />} />
          <Route path="/admin/OrganizationsManager" element={<OrganizationsManager />} />
          <Route path="/admin/ChangePassword" element={<ChangePassword />} />
          
          {/* Protected Admin Routes with admin layout if needed */}
          <Route path="/admin/dashboard" element={
            <PrivateRoute>
              <AdminDashboard />
            </PrivateRoute>
          } />
          
          <Route path="/admin/menumanager" element={
            <PrivateRoute>
              <MenuManager />
            </PrivateRoute>
          } />
          
          {/* ========== PUBLIC ROUTES (With Navbar/Footer/AnnouncementPopup) ========== */}
          
          {/* Root path - Home page */}
          <Route path="/" element={
            <PublicLayout>
              <Home />
            </PublicLayout>
          } />
          
          {/* If you want to also support /public_web as an alias */}
          <Route path="/public_web" element={<Navigate to="/" />} />
          
          <Route path="/about" element={
            <PublicLayout>
              <About />
            </PublicLayout>
          } />

          <Route path="/OrganizationPublic" element={
            <PublicLayout>
              <OrganizationPublic />
            </PublicLayout>
          } />
          
          <Route path="/History" element={
            <PublicLayout>
              <History />
            </PublicLayout>
          } />
          
          <Route path="/PublicSchoolInfo" element={
            <PublicLayout>
              <PublicSchoolInfo />
            </PublicLayout>
          } />
          
          <Route path="/PublicAnnouncements" element={
            <PublicLayout>
              <PublicAnnouncements />
            </PublicLayout>
          } />

          <Route path="/ServiceDetail" element={
            <PublicLayout>
              <ServiceDetail />
            </PublicLayout>
          } />
          
          <Route path="/LeadershipMessages" element={
            <PublicLayout>
              <LeadershipMessages />
            </PublicLayout>
          } />

          <Route path="/AcademicEvents" element={
            <PublicLayout>
              <AcademicEvents />
            </PublicLayout>
          } />
          
          <Route path="/OwnerProfile" element={
            <PublicLayout>
              <OwnerProfile />
            </PublicLayout>
          } />
          
          <Route path="/ProjectsPage" element={
            <PublicLayout>
              <ProjectsPage />
            </PublicLayout>
          } />
          
          <Route path="/departments" element={
            <PublicLayout>
              <Departments />
            </PublicLayout>
          } />
          
          <Route path="/ProspectusView" element={
            <PublicLayout>
              <ProspectusView />
            </PublicLayout>
          } />
          
          <Route path="/VisionMission" element={
            <PublicLayout>
              <VisionMission />
            </PublicLayout>
          } />
          
          <Route path="/courses" element={
            <PublicLayout>
              <Courses />
            </PublicLayout>
          } />
          
          <Route path="/CalendarOfEvents" element={
            <PublicLayout>
              <CalendarOfEvents />
            </PublicLayout>
          } />

          <Route path="/PublicFacilities" element={
            <PublicLayout>
              <PublicFacilities />
            </PublicLayout>
          } />

          <Route path="/StaffPage" element={
            <PublicLayout>
              <StaffPage />
            </PublicLayout>
          } />
          
          <Route path="/faculty" element={
            <PublicLayout>
              <Faculty />
            </PublicLayout>
          } />
          
          <Route path="/AdmissionProcedureDetails" element={
            <PublicLayout>
              <AdmissionProcedureDetails />
            </PublicLayout>
          } />
          
          <Route path="/events" element={
            <PublicLayout>
              <Events />
            </PublicLayout>
          } />
          
          <Route path="/gallery" element={
            <PublicLayout>
              <Gallery />
            </PublicLayout>
          } />
          
          <Route path="/contact" element={
            <PublicLayout>
              <Contact />
            </PublicLayout>
          } />
          
          {/* ========== REDIRECTS ========== */}
          <Route path="/admin" element={<Navigate to="/admin/dashboard" />} />
          
          {/* 404 Page */}
          <Route path="*" element={
            <PublicLayout>
              <div className="container py-5 text-center">
                <h1 className="display-1">404</h1>
                <h2 className="mb-4">Page Not Found</h2>
                <p className="lead">The page you are looking for doesn't exist.</p>
                <a href="/" className="btn btn-primary">Go Home</a>
              </div>
            </PublicLayout>
          } />
        </Routes>
      </div>
    </Router>
  );
}

export default App;